import { AssessmentResult } from '../../types/assessment';
import { scoreClass } from './scoreHelpers';
import { computeScores } from './computeScores';
import TiCard from '../common/TiCard';

interface Props {
  result: AssessmentResult;
}

export default function DetailedScorecardCard({ result }: Props) {
  const { scores, is_opinion } = result;
  const qb = result.quality_breakdown;
  const vb = result.veracity_breakdown;
  const vaB = result.valence_breakdown;
  const mb = result.manipulation_breakdown;
  const c = computeScores(result);

  return (
    <TiCard animate fullHeight>
      {/* Quality breakdown */}
      {!is_opinion && scores.quality != null && (
        <>
          <div className="section-label">Detailed scorecard — quality index &middot; {c.qualityTotal}/100</div>
          <div className="score-row"><span>Completeness &amp; context (23)</span><span className={`score-val ${scoreClass(qb.completeness ?? 0, 23)}`}>{qb.completeness}</span></div>
          <div className="score-row"><span>Balance &amp; fairness (18)</span><span className={`score-val ${scoreClass(qb.balance ?? 0, 18)}`}>{qb.balance}</span></div>
          <div className="score-row"><span>Depth &amp; thoroughness (18)</span><span className={`score-val ${scoreClass(qb.depth ?? 0, 18)}`}>{qb.depth}</span></div>
          <div className="score-row"><span>Editorial standards (18)</span>
            <span className={`score-val ${scoreClass(c.editorialTotal, 18)}`}>
              {c.editorialTotal}
            </span>
          </div>
          <div className="sub-row"><span>Professional presentation (5)</span><span>{qb.editorial_presentation}</span></div>
          <div className="sub-row"><span>Correction practices (8)</span><span>{qb.editorial_corrections}</span></div>
          <div className="sub-row"><span>Temporal transparency (5)</span><span>{qb.editorial_temporal}</span></div>
          {qb.temporal_penalty !== 0 && (
            <div className="sub-row"><span>Temporal penalty</span><span className="val-red">{qb.temporal_penalty}</span></div>
          )}
          <div className="score-row"><span>Transparency &amp; accountability (18)</span><span className={`score-val ${scoreClass(qb.transparency ?? 0, 18)}`}>{qb.transparency}</span></div>
          <div className="score-row"><span>Content authenticity (5)</span>
            <span className={`score-val ${scoreClass(c.authenticityTotal, 5)}`}>
              {c.authenticityTotal}
            </span>
          </div>
          <div className="sub-row"><span>AI disclosure (3)</span><span>{qb.authenticity_ai_disclosure}</span></div>
          <div className="sub-row"><span>Provenance verification (2)</span><span>{qb.authenticity_provenance}</span></div>
          {qb.article_type_notes && <div className="note">{qb.article_type_notes}</div>}
          <hr className="ti-divider" />
        </>
      )}

      {/* Veracity breakdown */}
      <div className="section-label">
        Source veracity &middot; {c.veracityArticle}/100 article &middot; {scores.veracity_adjustment >= 0 ? '+' : ''}{scores.veracity_adjustment} publisher = {c.veracityFinal}/100 (scaled)
      </div>
      <div className="score-row"><span>Source identification (20)</span><span className={`score-val ${scoreClass(c.sourceId, 20)}`}>{c.sourceId}</span></div>
      <div className="sub-row"><span>Named sources (10)</span><span>{vb.named_sources}</span></div>
      <div className="sub-row"><span>Attribution clarity (10)</span><span>{vb.attribution_clarity}</span></div>
      <div className="score-row"><span>Source quality &amp; type (25)</span><span className={`score-val ${scoreClass(c.sourceQuality, 25)}`}>{c.sourceQuality}</span></div>
      <div className="sub-row"><span>Primary sources (10)</span><span>{vb.primary_sources}</span></div>
      <div className="sub-row"><span>Expert sources (8)</span><span>{vb.expert_sources}</span></div>
      <div className="sub-row"><span>Source independence (7)</span><span>{vb.source_independence}</span></div>
      <div className="score-row"><span>Source framing &amp; context (25)</span><span className={`score-val ${scoreClass(c.sourceFraming, 25)}`}>{c.sourceFraming}</span></div>
      <div className="sub-row"><span>Context provided (10)</span><span>{vb.context_provided}</span></div>
      <div className="sub-row"><span>Appropriate skepticism (8)</span><span>{vb.appropriate_skepticism}</span></div>
      <div className="sub-row"><span>Conflicting sources (7)</span><span>{vb.conflicting_sources}</span></div>
      <div className="score-row"><span>Lateral verification (20)</span><span className={`score-val ${scoreClass(c.lateralVerification, 20)}`}>{c.lateralVerification}</span></div>
      <div className="sub-row"><span>Multiple confirmation (10)</span><span>{vb.multiple_confirmation}</span></div>
      <div className="sub-row"><span>Cross-reference (10)</span><span>{vb.cross_reference}</span></div>
      <div className="score-row"><span>Source accessibility (10)</span><span className={`score-val ${scoreClass(c.sourceAccessibility, 10)}`}>{c.sourceAccessibility}</span></div>
      <div className="sub-row"><span>Links (5)</span><span>{vb.links}</span></div>
      <div className="sub-row"><span>Retrievability (5)</span><span>{vb.retrievability}</span></div>
      {vb.publisher_research && <div className="note">{vb.publisher_research}</div>}

      {/* Valence breakdown */}
      {!is_opinion && scores.valence != null && vaB.subtotal_50 != null && (
        <>
          <hr className="ti-divider" />
          <div className="section-label">Feeling valence &middot; {c.valenceTotal}/100 (subtotal {c.valenceAdjusted50}/50 &times; 2)</div>
          <div className="score-row">
            <span>Emotional intensity (20) — {vaB.intensity_level}, VADER est. ~{vaB.intensity_vader_estimate}</span>
            <span className={`score-val ${scoreClass(vaB.intensity_pts ?? 0, 20)}`}>{vaB.intensity_pts}</span>
          </div>
          <div className="score-row">
            <span>Emotional valence type (15) — {vaB.dominant_emotion}</span>
            <span className={`score-val ${scoreClass(vaB.valence_type_pts ?? 0, 15)}`}>{vaB.valence_type_pts}</span>
          </div>
          {vaB.valence_type_base != null && (
            <>
              <div className="sub-row"><span>Base</span><span>{vaB.valence_type_base}</span></div>
              {vaB.valence_type_adjustment != null && vaB.valence_type_adjustment !== 0 && (
                <div className="sub-row"><span>Context adjustment{vaB.context_note ? ` (${vaB.context_note})` : ''}</span><span>+{vaB.valence_type_adjustment}</span></div>
              )}
            </>
          )}
          <div className="score-row">
            <span>Cumulative load (10) — avg ~{vaB.avg_load_estimate}, {vaB.distribution_pattern}</span>
            <span className={`score-val ${scoreClass(vaB.cumulative_load_pts ?? 0, 10)}`}>{vaB.cumulative_load_pts}</span>
          </div>
          <div className="score-row">
            <span>Intensity amplifiers (5) — {vaB.amplifiers_detected || 'none detected'}</span>
            <span className={`score-val ${scoreClass(vaB.amplifiers_pts ?? 0, 5)}`}>{vaB.amplifiers_pts}</span>
          </div>
          <div className="score-row">
            <span>Subtotal ({c.valenceSubtotal50}/50){(vaB.article_type_adjustment ?? 0) !== 0 ? ` + type adj +${vaB.article_type_adjustment}` : ''}</span>
            <span className="score-val">{c.valenceAdjusted50}/50 &times; 2 = {c.valenceTotal}</span>
          </div>
        </>
      )}

      {/* Manipulation breakdown */}
      <hr className="ti-divider" />
      <div className="section-label">Manipulation index &middot; {c.manipulationTotal}/100</div>
      <div className="score-row"><span>Base</span><span className="score-val">100</span></div>
      <div className="score-row"><span>Manipulative language (max −20)</span>
        <span className={`score-val ${mb.loaded_language !== 0 ? 'val-orange' : 'val-green'}`}>{mb.loaded_language === 0 ? '0' : `−${Math.abs(mb.loaded_language)}`}</span>
      </div>
      {mb.loaded_language_instances && <div className="sub-row"><span>{mb.loaded_language_instances}</span><span>−{Math.abs(mb.loaded_language)}</span></div>}
      <div className="score-row"><span>Logical fallacies (max −15)</span>
        <span className={`score-val ${mb.logical_fallacies !== 0 ? 'val-orange' : 'val-green'}`}>{mb.logical_fallacies === 0 ? '0' : `−${Math.abs(mb.logical_fallacies)}`}</span>
      </div>
      {mb.logical_fallacies_instances && <div className="sub-row"><span>{mb.logical_fallacies_instances}</span><span>−{Math.abs(mb.logical_fallacies)}</span></div>}
      <div className="score-row"><span>Diversion tactics (max −10)</span>
        <span className={`score-val ${mb.diversion !== 0 ? 'val-orange' : 'val-green'}`}>{mb.diversion === 0 ? '0' : `−${Math.abs(mb.diversion)}`}</span>
      </div>
      {mb.diversion_instances && <div className="sub-row"><span>{mb.diversion_instances}</span><span>−{Math.abs(mb.diversion)}</span></div>}
      <div className="score-row"><span>Social &amp; emotional pressure (max −5)</span>
        <span className={`score-val ${mb.social_pressure !== 0 ? 'val-orange' : 'val-green'}`}>{mb.social_pressure === 0 ? '0' : `−${Math.abs(mb.social_pressure)}`}</span>
      </div>
      {mb.social_pressure_instances && <div className="sub-row"><span>{mb.social_pressure_instances}</span><span>−{Math.abs(mb.social_pressure)}</span></div>}
      <div className="score-row"><span>Cognitive bias exploitation (max −10)</span>
        <span className={`score-val ${mb.cognitive_bias !== 0 ? 'val-orange' : 'val-green'}`}>{mb.cognitive_bias === 0 ? '0' : `−${Math.abs(mb.cognitive_bias)}`}</span>
      </div>
      {mb.cognitive_bias_instances && <div className="sub-row"><span>{mb.cognitive_bias_instances}</span><span>−{Math.abs(mb.cognitive_bias)}</span></div>}
      <div className="score-row"><span>Dehumanization</span>
        <span className={`score-val ${mb.dehumanization !== 0 ? 'val-red' : 'val-green'}`}>{mb.dehumanization === 0 ? '0 — not detected' : `−${Math.abs(mb.dehumanization)}`}</span>
      </div>
      {mb.notes && <div className="note">{mb.notes}</div>}
    </TiCard>
  );
}
